import { useEffect, useState } from 'react';
import { X, ArrowRight, CheckCircle2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Persona-aware contact modal. Opened from the "Every journey starts
 * somewhere" cards on the homepage. Submissions land in the same admin
 * Lead Management pipeline (source = "website") with the persona label
 * pre-tagged in `program_interest` + `notes` for context.
 */
export default function PersonaContactModal({ open, onClose, persona, personaDesc }) {
  const [form, setForm] = useState({ name: '', email: '', phone: '', notes: '' });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (open) {
      setForm({ name: '', email: '', phone: '', notes: '' });
      setDone(false);
      setErr('');
      const original = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = original; };
    }
  }, [open]);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    if (!form.name.trim() || (!form.email.trim() && !form.phone.trim())) {
      setErr('Please share your name and either an email or phone.');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/leads/public`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          phone: form.phone,
          notes: form.notes,
          persona,
        }),
      });
      if (!res.ok) throw new Error('Submission failed');
      setDone(true);
    } catch (_) {
      setErr('Something went wrong. Please try again or email info@trueglobaleducation.com.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-brand-obsidian/50 backdrop-blur-sm"
      onClick={onClose}
      data-testid="persona-contact-modal"
    >
      <div
        className="relative bg-white rounded-3xl w-full max-w-2xl overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-5 right-5 p-2 rounded-full hover:bg-brand-mist text-brand-obsidian/60 hover:text-brand-obsidian transition-colors z-10"
          aria-label="Close"
          data-testid="persona-modal-close"
        >
          <X size={18} />
        </button>

        <div className="grid grid-cols-1 md:grid-cols-5 font-sans-clean">
          <div className="md:col-span-2 bg-brand-pine text-white p-8 md:p-9">
            <div className="text-[10px] uppercase tracking-[0.22em] text-brand-coral font-bold mb-4">
              Your Journey
            </div>
            <div className="font-serif-editorial text-2xl leading-snug">{persona}</div>
            {personaDesc && (
              <p className="text-sm text-white/70 mt-4 leading-relaxed">{personaDesc}</p>
            )}
            <div className="mt-10 pt-8 border-t border-white/15 text-xs text-white/60 leading-relaxed">
              Share a few details and an advisor will reach out within one business day. No pressure, no cost.
            </div>
          </div>

          <div className="md:col-span-3 p-8 md:p-9">
            {done ? (
              <div className="py-6 text-center" data-testid="persona-modal-success">
                <div className="w-12 h-12 mx-auto rounded-full bg-brand-pine/10 flex items-center justify-center">
                  <CheckCircle2 size={22} className="text-brand-pine" />
                </div>
                <h3 className="font-serif-editorial text-2xl mt-5">Got it.</h3>
                <p className="text-sm text-brand-slate mt-3 leading-relaxed max-w-sm mx-auto">
                  Your details are with our advisors. Expect a friendly follow-up within one business day.
                </p>
                <button
                  onClick={onClose}
                  className="mt-6 text-sm font-semibold text-brand-pine hover:text-brand-coral-deep transition-colors"
                  data-testid="persona-modal-done"
                >
                  Close
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4" data-testid="persona-contact-form">
                <div className="text-[11px] uppercase tracking-[0.22em] text-brand-pine font-bold">
                  Tell us about you
                </div>
                <div>
                  <label className="text-xs font-semibold text-brand-obsidian/60 mb-1.5 block">Full name</label>
                  <input
                    required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="w-full border border-brand-border/60 rounded-lg px-3.5 py-2.5 text-sm focus:outline-none focus:border-brand-pine transition-colors"
                    data-testid="persona-name"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-brand-obsidian/60 mb-1.5 block">Email</label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="w-full border border-brand-border/60 rounded-lg px-3.5 py-2.5 text-sm focus:outline-none focus:border-brand-pine transition-colors"
                    data-testid="persona-email"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-brand-obsidian/60 mb-1.5 block">Phone</label>
                  <input
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    className="w-full border border-brand-border/60 rounded-lg px-3.5 py-2.5 text-sm focus:outline-none focus:border-brand-pine transition-colors"
                    data-testid="persona-phone"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-brand-obsidian/60 mb-1.5 block">Anything else? (optional)</label>
                  <textarea
                    rows={3}
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    placeholder="e.g. preferred field, timing, city…"
                    className="w-full border border-brand-border/60 rounded-lg px-3.5 py-2.5 text-sm focus:outline-none focus:border-brand-pine transition-colors resize-none"
                    data-testid="persona-notes"
                  />
                </div>
                {err && <div className="text-xs text-red-600" data-testid="persona-error">{err}</div>}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full inline-flex items-center justify-center gap-2 bg-brand-coral hover:bg-brand-coral-deep text-white text-sm font-semibold px-6 py-3 rounded-full transition-colors disabled:opacity-60"
                  data-testid="persona-submit"
                >
                  {loading ? 'Sending…' : 'Talk to an advisor'} <ArrowRight size={15} />
                </button>
                <p className="text-[11px] text-brand-obsidian/50 leading-relaxed text-center pt-1">
                  By submitting you agree to be contacted by True Global Education.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
